import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import store from './config/store/';
import * as registerServiceWorker from './registerServiceWorker';

const ArticleRating = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ArticleRating" */ './components/article_rating/index'
    ),
);
const ArticleRatingPercentage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ArticleRatingPercentage" */ './components/article_rating_percentage/index'
    ),
);

const MobileAppArticleRating = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MobileAppArticleRating" */ './components/mobile_app_article_rating/index'
    ),
);

const DocumentDownload = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DocumentDownload", webpackPreload: true */ './components/document'
    ),
);

const Footer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Footer", webpackPrefetch: true */ './components/newsletters/footer'
    ),
);

const Intext = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Intext", webpackPrefetch: true */ './components/newsletters/intext'
    ),
);

const IntextBoxes = React.lazy(
  () =>
    import(
      /* webpackChunkName: "IntextBoxes", webpackPrefetch: true */ './components/newsletters/intextBoxes'
    ),
);

const Landing = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Landing", webpackPreload: true */ './components/newsletters/landing'
    ),
);

const MetaData = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MetaData", webpackPreload: true */ './components/metaData'
    ),
);

const Overlay = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Overlay", webpackPrefetch: true */ './components/newsletters/overlay'
    ),
);

const PageCounter = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PageCounter", webpackPreload: true */ './components/page_counter'
    ),
);

const Rating = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Rating", webpackPrefetch: true */ './components/rating'
    ),
);
const RatingRedesign = React.lazy(
  () =>
    import(
      /* webpackChunkName: "RatingRedesign", webpackPrefetch: true */ './components/rating_redesign'
    ),
);

const WrongEmailSent = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WrongEmailSent" */ './components/newsletters/wrongEmailSent'
    ),
);
const CurrentEmail = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CurrentEmail" */ './components/newsletters/currentEmail'
    ),
);

const DeleteCookieLink = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DeleteCookieLink" */ './components/newsletters/deleteCookieLink'
    ),
);

const SignupForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SignupForm" */ './components/newsletters/signupForm'
    ),
);

const StickySignupForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StickySignupForm" */ './components/newsletters/stickySignupForm'
    ),
);

const CombinedCallDepositCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CombinedCallDepositCalc" */ './components/calculators/CombinedCallDepositCalc'
    ),
);

const BudgetCalculatorCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "BudgetCalculatorCalc" */ './components/calculators/BudgetCalculatorCalc'
    ),
);

const LongTermCareContributionCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LongTermCareContributionCalc" */ './components/calculators/LongTermCareContributionCalc'
    ),
);

const EtfFinderCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "EtfFinderCalc" */ './components/calculators/EtfFinderCalc'
    ),
);

const AdvanceFlatRateCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AdvanceFlatRateCalc" */ './components/calculators/AdvanceFlatRateCalc'
    ),
);
const InheritanceTaxCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "InheritanceTaxCalc" */ './components/calculators/InheritanceTaxCalculator'
    ),
);
const SearchResultsComponent = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SearchResultsComponent" */ './components/search_results'
    ),
);
const HumanCapitalcalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HumanCapitalcalc" */ './components/calculators/HumanCapitalcalc'
    ),
);

const GiftTaxCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "GiftTaxCalc" */ './components/calculators/GiftTaxCalc'
    ),
);

const Co2CostCalculatorCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "Co2CostCalculatorCalc" */ './components/calculators/Co2CostCalculator'
    ),
);

const WithdrawalCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "WithdrawalCalc" */ './components/calculators/WithdrawalCalc'
    ),
);

const ComparisonTable = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ComparisonTable" */ './components/comparison_table'
    ),
);

const PayPalCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PayPalCalc" */ './components/calculators/PayPalCalc'
    ),
);

const RentOrBuyCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "RentOrBuyCalc" */ './components/calculators/RentOrBuyCalc'
    ),
);

const PartnerBudgetCalculatorCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PartnerBudgetCalculatorCalc" */ './components/calculators/PartnerBudgetCalc'
    ),
);

const HouseHoldCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HouseHoldCalc" */ './components/calculators/HouseHoldCalc'
    ),
);

const PensionDeductionCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PensionDeductionCalc" */ './components/calculators/PensionDeductionCalc'
    ),
);

const HousePurchaseAdditionalCostsCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "HousePurchaseAdditionalCostsCalc" */ './components/calculators/HousePurchaseAdditionalCostsCalc'
    ),
);

const DivorceCalc = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DivorceCalc" */ './components/calculators/DivorceCalculator'
    ),
);

const MetaInfo = React.lazy(
  () => import(/* webpackChunkName: "MetaInfo" */ './components/metaInfo'),
);

GenerateComponents([
  ['react-article-rating', ArticleRating],
  ['react-mobile-app-article-rating', MobileAppArticleRating],
  ['react-article-rating-percentage', ArticleRatingPercentage],
  ['react-newsletter-signupwall', DocumentDownload],
  ['react-newsletter-footer', Footer],
  ['react-newsletter-intext', Intext],
  ['react-newsletter-intext-boxes', IntextBoxes],
  ['react-newsletter-landing', Landing],
  ['react-newsletter-overlay', Overlay],
  ['react-meta-data', MetaData],
  ['react-page-counter', PageCounter],
  ['react-newsletter-rating', Rating],
  ['react-newsletter-rating-redesign', RatingRedesign],
  ['react-newsletter-wrong-email-sent', WrongEmailSent],
  ['react-newsletter-current-email', CurrentEmail],
  ['react-newsletter-delete-cookie-link', DeleteCookieLink],
  ['react-newsletter-signupform', SignupForm],
  ['react-newsletter-sticky-signupform', StickySignupForm],
  ['react-combined-call-deposit-calculator', CombinedCallDepositCalc],
  ['react-budget-calculator', BudgetCalculatorCalc],
  [
    'react-long-term-care-contribution-calculator',
    LongTermCareContributionCalc,
  ],
  ['react-etf-finder-calculator', EtfFinderCalc],
  ['react-advance-flat-rate-calculator', AdvanceFlatRateCalc],
  ['react-inheritance-tax-calculator', InheritanceTaxCalc],
  ['react-human-capital-calculator', HumanCapitalcalc],
  ['react-gift-tax-calculator', GiftTaxCalc],
  ['react-search-results', SearchResultsComponent],
  ['react-co2-cost-calculator', Co2CostCalculatorCalc],
  ['react-pay-pal-calculator', PayPalCalc],
  ['react-withdrawal-calculator', WithdrawalCalc],
  ['react-comparison-table', ComparisonTable],
  ['react-rent-or-buy-calculator', RentOrBuyCalc],
  ['react-wl-budget-calculator', PartnerBudgetCalculatorCalc],
  ['react-household-appliances-calculator', HouseHoldCalc],
  ['react-pension-deduction-calculator', PensionDeductionCalc],
  ['react-house-purchase-additional-costs', HousePurchaseAdditionalCostsCalc],
  ['react-divorce-calculator', DivorceCalc],
  ['react-meta-info', MetaInfo],
]);

function GenerateComponents(component_list) {
  component_list.forEach((component) => {
    [...document.getElementsByClassName(component[0])].forEach(
      (element, index) => {
        let generatedId = `${component[0]}-${index}`,
          Component = component[1];

        element.setAttribute('id', generatedId);
        ReactDOM.render(
          <Provider store={store}>
            <Suspense fallback={<div></div>}>
              <Component
                elementIndex={index}
                mainId={generatedId}
                {...element.dataset}
              />
            </Suspense>
          </Provider>,
          element,
        );
      },
    );
  });
}

registerServiceWorker.unregister();
